import type { TransactionScheduleCallbacks } from "@hooks/view/useViewTableMeta";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { formatISO } from "date-fns";
import { Scrollbars } from "react-custom-scrollbars-2";
import { TRANSFER_MATCH_ACTION } from "@common/requiredActions";
import { TransactionStatus } from "@common/types/transaction";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getMatchedDocumentFieldLock } from "@common/helpers/matched-document-transaction-fields";
import {
    isCategoryTaxonomyReviewEligible,
    isLedgerAccountReviewEligible,
} from "@common/helpers/transactionFieldReviewEligibility";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toDisplayDateOnly } from "@helpers/date";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTransactionLedgerAccount } from "@hooks/useTransactionLedgerAccount";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import { Loader } from "@design-system/Loader/Loader";
import "./TransactionDetails.scss";
import { Button } from "@design-system/Button/Button";
import { DetailsAsideViewportOffsetContext } from "@design-system/DetailsAside/DetailsAside.context";
import { RemoveIcon } from "@icons";
import {
    addTransactionFieldReviews,
    revokeTransactionFieldReview,
} from "@api/transaction.api";
import { useHasAccrualLedger } from "@api/ledger.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { TransactionFieldReviewDto } from "@common/schemas/transactionFieldReview.schema";
import { WorkspaceMember } from "@common/types/workspace";
import { useUser } from "@hooks/useUser";
import { TransactionEntity } from "../TransactionEntity";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { TransactionDetailsHeader } from "./TransactionDetailsHeader";
import { TransactionDetailsMiscFields } from "./TransactionDetailsMiscFields";
import { TransactionDetailsCategory } from "./TransactionDetailsCategory";
import { TransactionDetailsAccount } from "./TransactionDetailsAccount";
import { TransactionDetailsFooter } from "./TransactionDetailsFooter";
import { TransactionActionsSection } from "./TransactionActionsSection";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsHistoryNew } from "./TransactionDetailsHistory/TransactionDetailsHistoryNew";
import { useTransactionDisplayedLogs } from "./TransactionDetailsHistory/useTransactionDisplayedLogs";
import { useApplicableTaxQuestions } from "./useApplicableTaxQuestions";
import {
    TransactionDetailsReview,
    type TransactionFieldReviewState,
    type TransactionFieldReviewer,
} from "./TransactionDetailsReview";
import { invalidateTransactionDetailsQueries } from "./useTransactionDetailsQuery";

// Owns only the accordion's open/closed state. It is initialized once from
// `initialOpenPanels` and is otherwise driven purely by user interaction.
// Remount it (via `key`) to reset which panels are open.
const TransactionDetailsAccordion: React.FC<{
    initialOpenPanels: string[];
    children: React.ReactNode;
}> = ({ initialOpenPanels, children }) => {
    const [openPanels, setOpenPanels] = useState(initialOpenPanels);
    const onValueChange = useCallback(
        (value: string[]) => setOpenPanels(value),
        [],
    );
    return (
        <AccordionRoot value={openPanels} onValueChange={onValueChange}>
            {children}
        </AccordionRoot>
    );
};

const EMPTY_REVIEW_STATE: TransactionFieldReviewState = {
    counterparty: [],
    category: [],
    class: [],
    entity: [],
    cashLedger: [],
    accrualLedger: [],
    note: [],
};

function buildReviewers(
    reviews: TransactionFieldReviewDto[],
    members: WorkspaceMember[],
    field: TransactionReviewField,
): TransactionFieldReviewer[] {
    return reviews
        .filter((r) => r.field === field)
        .map((r) => {
            const member = members.find((m) => m.user.id === r.userId);
            return {
                userId: r.userId,
                name: member?.user.name ?? `User ${r.userId}`,
                reviewedAt: formatISO(r.createdAt),
                avatarUrl: member?.user.avatar ?? null,
                iconColor: member?.user.iconColor ?? "",
                email: member?.user.email ?? null,
            };
        });
}

const TransactionReviewAccordionItem: React.FC<{
    transaction: NonNullable<
        React.ContextType<typeof TransactionDetailsContext>["transaction"]
    >;
    isGlFirst: boolean;
}> = ({ transaction, isGlFirst }) => {
    const { data: workspaceMembers = [] } = useCurrentWorkspaceMembers();
    const currentUser = useUser();
    const isReviewOperationInFlightRef = useRef(false);
    const hasAccrualLedger = useHasAccrualLedger(transaction.entity.id);

    const isCategoryEligible = isCategoryTaxonomyReviewEligible(
        transaction.category?.taxonomy,
    );

    // Resolve the effective cash/accrual ledger account the same way
    // LedgerCell does, so "Mark reviewed" is hidden for a blank ledger or an
    // account resolving to an "Uncategorized Income/Expenses" placeholder —
    // mirroring the backend's eligibility check.
    const { account: cashAccount } = useTransactionLedgerAccount(transaction, {
        includeCurrentAccount: true,
        currentAccountId: transaction.accountId ?? undefined,
        basis: AccountingLedgerBasis.cash,
    });
    const { account: accrualAccount } = useTransactionLedgerAccount(
        transaction,
        {
            includeCurrentAccount: true,
            currentAccountId: transaction.accrualAccountId ?? undefined,
            basis: AccountingLedgerBasis.accruals,
        },
    );
    const isCashLedgerEligible = isLedgerAccountReviewEligible(
        cashAccount
            ? {
                  role: cashAccount.role ?? null,
                  kickOriginalCode: cashAccount.kickOriginalCode,
              }
            : null,
    );
    const isAccrualLedgerEligible = isLedgerAccountReviewEligible(
        accrualAccount
            ? {
                  role: accrualAccount.role ?? null,
                  kickOriginalCode: accrualAccount.kickOriginalCode,
              }
            : null,
    );

    const reviewState = useMemo((): TransactionFieldReviewState => {
        const reviews = transaction.fieldReviews;
        if (!reviews) {
            return EMPTY_REVIEW_STATE;
        }
        return {
            counterparty: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.COUNTERPARTY,
            ),
            category: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.CATEGORY,
            ),
            class: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.CLASS,
            ),
            entity: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.ENTITY,
            ),
            cashLedger: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.CASH_LEDGER,
            ),
            accrualLedger: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.ACCRUAL_LEDGER,
            ),
            note: buildReviewers(
                reviews,
                workspaceMembers,
                TransactionReviewField.NOTE,
            ),
        };
    }, [transaction.fieldReviews, workspaceMembers]);

    const handleMarkReviewed = useCallback(
        async (field: TransactionReviewField) => {
            if (isReviewOperationInFlightRef.current) {
                return;
            }
            isReviewOperationInFlightRef.current = true;
            try {
                const reviews = await addTransactionFieldReviews(
                    transaction.id,
                    [field],
                );
                const isSuperseded = (r: TransactionFieldReviewDto) =>
                    reviews.some(
                        (nr) => nr.field === r.field && nr.userId === r.userId,
                    );
                updateTransactionInCache(transaction.id, (t) => ({
                    ...t,
                    fieldReviews: [
                        ...(t.fieldReviews ?? []).filter(
                            (r) => !isSuperseded(r),
                        ),
                        ...reviews,
                    ],
                }));
                await invalidateTransactionDetailsQueries(transaction.id);
            } catch {
                // error toast handled by axios interceptor
            } finally {
                isReviewOperationInFlightRef.current = false;
            }
        },
        [transaction.id],
    );

    const handleUndoReview = useCallback(
        async (field: TransactionReviewField) => {
            if (isReviewOperationInFlightRef.current) {
                return;
            }
            isReviewOperationInFlightRef.current = true;
            try {
                await revokeTransactionFieldReview(transaction.id, field);
                updateTransactionInCache(transaction.id, (t) => ({
                    ...t,
                    fieldReviews: (t.fieldReviews ?? []).filter(
                        (r) =>
                            !(r.userId === currentUser.id && r.field === field),
                    ),
                }));
                await invalidateTransactionDetailsQueries(transaction.id);
            } catch {
                // error toast handled by axios interceptor
            } finally {
                isReviewOperationInFlightRef.current = false;
            }
        },
        [transaction.id, currentUser.id],
    );

    return (
        <DetailsAccordionItem value="review" label="Review" inset="wide">
            <TransactionDetailsReview
                reviewState={reviewState}
                currentUserId={currentUser.id}
                onMarkReviewed={handleMarkReviewed}
                onUndoReview={handleUndoReview}
                hasAccrualLedger={hasAccrualLedger}
                isGlFirst={isGlFirst}
                isCategoryEligible={isCategoryEligible}
                isCashLedgerEligible={isCashLedgerEligible}
                isAccrualLedgerEligible={isAccrualLedgerEligible}
            />
        </DetailsAccordionItem>
    );
};

interface Props {
    onClose(): void;
    hideFooter?: boolean;
    hideHistory?: boolean;
    hideDetails?: boolean;
    hideCloseButton?: boolean;
    initialOpenPanels?: string[];
    scheduleCallbacks?: TransactionScheduleCallbacks;
}

export const TransactionDetails: React.FC<Props> = ({
    onClose,
    hideFooter,
    hideHistory,
    hideDetails,
    hideCloseButton,
    initialOpenPanels,
    scheduleCallbacks,
}) => {
    const { transaction, memoRequired, receiptRequired } = useContext(
        TransactionDetailsContext,
    );
    const viewportOffsetPx = useContext(DetailsAsideViewportOffsetContext) ?? 0;
    const entityId = transaction?.entity.id ?? null;
    const isLocked = useIsDateLocked(
        entityId,
        transaction
            ? toDisplayDateOnly(transaction.dateOnly ?? transaction.date)
            : undefined,
    );
    const { isLocked: isMatchLocked, tooltip: matchLockTooltip } =
        getMatchedDocumentFieldLock({
            matchLists: [
                transaction?.transactionMatches,
                transaction?.accrualMatches,
            ],
            isOtherwiseLocked: isLocked,
        });
    const { enabled: isGlFirst } = useIsGlFirst();

    const displayedLogs = useTransactionDisplayedLogs(transaction);
    const hasHistory = !hideHistory && displayedLogs.length > 0;

    const applicableTaxQuestions = useApplicableTaxQuestions(transaction);

    const actionsForceOpen = useMemo(() => {
        if (!transaction) {
            return false;
        }
        const transferMatchRequired = transaction.requiredActions.includes(
            TRANSFER_MATCH_ACTION,
        );

        return Boolean(
            memoRequired ||
            receiptRequired ||
            applicableTaxQuestions.length > 0 ||
            transferMatchRequired ||
            transaction.isIgnored ||
            transaction.requirementsSnoozed,
        );
    }, [transaction, memoRequired, receiptRequired, applicableTaxQuestions]);

    // The single input that decides which panels open initially: an explicit
    // request (e.g. the review CTA) wins as-is; otherwise the details panel,
    // plus the actions panel when the transaction has required actions.
    const requestedOpenPanels = useMemo(() => {
        if (initialOpenPanels) {
            return initialOpenPanels;
        }
        return actionsForceOpen ? ["details", "actions"] : ["details"];
    }, [initialOpenPanels, actionsForceOpen]);

    return (
        <section
            className={classNames(
                `transaction-details transaction-details--${transaction?.status}`,
                {
                    "transaction-details--credit":
                        transaction && transaction.amount > 0,
                    "transaction-details--debit":
                        transaction && transaction.amount < 0,
                    "transaction-details--requirements-ignored":
                        transaction?.requirementsSnoozed,
                },
            )}
        >
            {transaction ? (
                <Scrollbars
                    autoHeight
                    autoHeightMax={`calc(100vh - ${viewportOffsetPx}px)`}
                >
                    <header className="transaction-details__header">
                        <TransactionDetailsHeader
                            transaction={transaction}
                            isLocked={isLocked}
                        />
                        <TransactionEntity
                            isLocked={isLocked}
                            transaction={transaction}
                        />
                        {!hideCloseButton && (
                            <Tooltip shortcut="escape" side="bottom">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={onClose}
                                    iconOnly
                                    aria-label="Close transaction details"
                                    style={{
                                        top: 12,
                                        right: 16,
                                        position: "absolute",
                                    }}
                                    data-testid="transaction-details-close"
                                >
                                    <RemoveIcon />
                                </Button>
                            </Tooltip>
                        )}
                    </header>
                    <main className="transaction-details__main">
                        {isGlFirst ? (
                            // GL-first workspaces classify by general ledger
                            // account, so the GL select takes the category
                            // slot. GL accounts only exist for posted
                            // transactions, so pending ones show nothing here.
                            transaction.status !==
                                TransactionStatus.PENDING && (
                                <TransactionDetailsAccount
                                    isLocked={isLocked || isMatchLocked}
                                    lockTooltip={matchLockTooltip}
                                    transaction={transaction}
                                    basis={AccountingLedgerBasis.cash}
                                    glFirstView
                                />
                            )
                        ) : (
                            <TransactionDetailsCategory
                                isLocked={isLocked}
                                transaction={transaction}
                            />
                        )}

                        <TransactionDetailsAccordion
                            key={`${transaction.id}:${requestedOpenPanels.join(",")}`}
                            initialOpenPanels={requestedOpenPanels}
                        >
                            <DetailsAccordionItem
                                value="actions"
                                label="Actions"
                                inset="wide"
                            >
                                <TransactionActionsSection
                                    transaction={transaction}
                                />
                            </DetailsAccordionItem>

                            {!hideDetails && (
                                <DetailsAccordionItem
                                    value="details"
                                    label="Transaction details"
                                    inset="wide"
                                >
                                    <TransactionDetailsMiscFields
                                        isLocked={isLocked}
                                        isMatchLocked={isMatchLocked}
                                        matchLockTooltip={matchLockTooltip}
                                        transaction={transaction}
                                        scheduleCallbacks={scheduleCallbacks}
                                    />
                                </DetailsAccordionItem>
                            )}

                            <TransactionReviewAccordionItem
                                transaction={transaction}
                                isGlFirst={isGlFirst}
                            />

                            {hasHistory && (
                                <DetailsAccordionItem
                                    value="history"
                                    label="Activity"
                                    inset="wide"
                                >
                                    <TransactionDetailsHistoryNew
                                        displayedLogs={displayedLogs}
                                    />
                                </DetailsAccordionItem>
                            )}
                        </TransactionDetailsAccordion>

                        {!hideFooter && (
                            <TransactionDetailsFooter
                                isLocked={isLocked}
                                transaction={transaction}
                                onClose={onClose}
                            />
                        )}
                    </main>
                </Scrollbars>
            ) : (
                <Loader />
            )}
        </section>
    );
};
